import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { TokenResponse, TokenCreate, TokenUpdate } from "@/lib/types";
import { api } from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";

interface TokensState {
  tokensByOrg: Record<string, TokenResponse[]>;
  setTokens: (organizationId: string, tokens: TokenResponse[]) => void;
  addToken: (organizationId: string, token: TokenResponse) => void;
  deleteToken: (organizationId: string, tokenId: string) => void;
  getToken: (tokenId: string) => TokenResponse | undefined;
  fetchTokensForCurrentOrg: () => Promise<void>;
  generateToken: (tokenData: TokenCreate) => Promise<TokenResponse>;
  revokeToken: (tokenId: string) => Promise<void>;
  updateToken: (tokenId: string, data: TokenUpdate) => Promise<void>;
}

export const useTokensStore = create<TokensState>()(
  immer((set, get) => ({
    tokensByOrg: {},

    setTokens: (organizationId, tokens) =>
      set((state) => {
        state.tokensByOrg[organizationId] = tokens;
      }),

    addToken: (organizationId, token) =>
      set((state) => {
        if (!state.tokensByOrg[organizationId]) {
          state.tokensByOrg[organizationId] = [];
        }
        state.tokensByOrg[organizationId].push(token);
      }),

    deleteToken: (organizationId, tokenId) =>
      set((state) => {
        if (state.tokensByOrg[organizationId]) {
          state.tokensByOrg[organizationId] = state.tokensByOrg[
            organizationId
          ].filter((t) => t.id !== tokenId);
        }
      }),

    getToken: (tokenId) => {
      const { tokensByOrg } = get();
      const selectedOrgId = 
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return undefined;
      return tokensByOrg[selectedOrgId]?.find((t) => t.id === tokenId);
    },

    fetchTokensForCurrentOrg: async () => {
      const selectedOrgId = 
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return;
      try {
        const tokens = await api.listTokens(selectedOrgId);
        set((state) => {
          state.tokensByOrg[selectedOrgId] = tokens;
        });
      } catch (error) {
        console.error("Failed to fetch tokens:", error);
      }
    },

    generateToken: async (tokenData: TokenCreate) => {
      try {
        const newToken = await api.generateToken(tokenData);
        set((state) => {
          const orgId = tokenData.organization_id;
          if (!state.tokensByOrg[orgId]) {
            state.tokensByOrg[orgId] = [];
          }
          state.tokensByOrg[orgId].push(newToken);
        });
        return newToken;
      } catch (error) {
        console.error("Failed to generate token:", error);
        throw error;
      }
    },

    revokeToken: async (tokenId: string) => {
      const selectedOrgId = 
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return;
      
      try {
        await api.revokeToken(tokenId);
        set((state) => {
          if (state.tokensByOrg[selectedOrgId]) {
            state.tokensByOrg[selectedOrgId] = state.tokensByOrg[selectedOrgId].map(token => 
              token.id === tokenId ? { ...token, is_active: false } : token
            );
          }
        });
      } catch (error) {
        console.error('Failed to revoke token:', error);
        throw error;
      }
    },

    updateToken: async (tokenId: string, data: TokenUpdate) => {
      const selectedOrgId = 
        useOrganizationsStore.getState().selectedOrganizationId;
      if (!selectedOrgId) return;

      try {
        const updatedToken = await api.updateToken(tokenId, data);
        set((state) => {
          if (state.tokensByOrg[selectedOrgId]) {
            state.tokensByOrg[selectedOrgId] = state.tokensByOrg[selectedOrgId].map(token =>
              token.id === tokenId ? { ...token, ...updatedToken } : token
            );
          }
        });
      } catch (error) {
        console.error('Failed to update token:', error);
        throw error;
      }
    },
  }))
);
